'use client';

import React, { useState, useMemo, useEffect, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import JourneyNav from '@/components/JourneyNav';
import Breadcrumb from '@/components/Breadcrumb';
import {
  NAMES_DATA,
  SUNNAT_TIMELINES,
  NAME_CATEGORIES,
  KEY_FILTERS,
  SEQUENCE_FILTERS,
  SEQUENCE_REGISTRY,
  type NameEntry,
  type SunnatTimeline,
} from './data/namesData';
import BookmarkButton from '@/components/BookmarkButton';
import SunnatSequenceBuilder from './components/SunnatSequenceBuilder';
import { ScrollReveal } from '@/components/PageTransition';
import { NamesReferenceFitraNote } from '../user-dashboard/components/Plot4GardenEnhancements';

// ─── Parallax Background for Names Hero ──────────────────────────────────────

function NamesParallaxBg() {
  const [offset, setOffset] = React.useState(0);

  React.useEffect(() => {
    function onScroll() {
      setOffset(window.scrollY * 0.35);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden',
      }}
    >
      {/* Floating Arabic Name glyphs */}
      {[
        { ar: 'الرَّحْمَٰن', x: '8%', baseY: 20, speed: 0.4, size: 28, opacity: 0.06 },
        { ar: 'الْقَدِير', x: '72%', baseY: 10, speed: 0.25, size: 22, opacity: 0.05 },
        { ar: 'الْعَلِيم', x: '45%', baseY: 60, speed: 0.5, size: 32, opacity: 0.04 },
        { ar: 'الْحَكِيم', x: '85%', baseY: 45, speed: 0.3, size: 20, opacity: 0.06 },
        { ar: 'اللَّطِيف', x: '25%', baseY: 75, speed: 0.45, size: 24, opacity: 0.05 },
        { ar: 'الْخَبِير', x: '60%', baseY: 30, speed: 0.2, size: 18, opacity: 0.04 },
      ].map((g, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: g.x,
            top: `calc(${g.baseY}% - ${offset * g.speed}px)`,
            fontFamily: "'Amiri', 'Georgia', serif",
            fontSize: g.size,
            color: `rgba(201,168,76,${g.opacity})`,
            direction: 'rtl',
            userSelect: 'none',
            transition: 'top 0.05s linear',
            whiteSpace: 'nowrap',
          }}
        >
          {g.ar}
        </div>
      ))}
      {/* Radial glow */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '60vw', height: '40vh',
        background: 'radial-gradient(ellipse, rgba(139,105,20,0.06) 0%, transparent 70%)',
      }} />
    </div>
  );
}

// ─── Name Frequency Badge ─────────────────────────────────────────────────────

function FrequencyBadge({ count }: { count: number }) {
  const size = count >= 10 ? 'large' : count >= 7 ? 'medium' : count >= 4 ? 'small' : 'minimal';
  const styles: Record<string, React.CSSProperties> = {
    large: { background: 'rgba(179,138,42,0.9)', color: '#1C2B25', fontWeight: 900 },
    medium: { background: 'rgba(139,105,20,0.3)', color: '#D4A82A', fontWeight: 700 },
    small: { background: 'rgba(44,74,62,0.4)', color: 'rgba(245,240,232,0.6)' },
    minimal: { background: 'rgba(28,43,37,0.5)', color: 'rgba(245,240,232,0.3)' },
  };
  return (
    <span className="font-mono text-[9px] px-2 py-0.5 rounded-sm" style={styles[size]}>
      {count}×
    </span>
  );
}

// ─── Letter Source Badge ──────────────────────────────────────────────────────

function LetterSourceBadge({ source }: { source: string }) {
  const isHadith = source === 'hadith-99';
  const is5923 = source === '59:23-24' || source === '59:23' || source === '59:24';
  const isSurahIkhlas = source === '112:1' || source === '112:2';
  const isSurahHadid = source === '57:3';
  const color = isHadith ? '#8a8ab8' : is5923 ? '#c9a84c' : isSurahIkhlas ? '#c9a84c' : isSurahHadid ? '#2d8a87' : 'rgba(201,168,76,0.6)';
  const bg = isHadith ? 'rgba(90,90,138,0.12)' : is5923 ? 'rgba(201,168,76,0.1)' : isSurahIkhlas ? 'rgba(201,168,76,0.08)' : isSurahHadid ? 'rgba(45,138,135,0.1)' : 'rgba(201,168,76,0.06)';
  const border = isHadith ? 'rgba(90,90,138,0.3)' : is5923 ? 'rgba(201,168,76,0.3)' : 'rgba(201,168,76,0.2)';
  const label = isHadith ? '99 Names Hadith' : `Quran ${source}`;
  return (
    <span
      className="font-mono text-[8px] px-1.5 py-0.5 rounded-sm"
      style={{ background: bg, border: `1px solid ${border}`, color, letterSpacing: '.06em' }}
      title={`Appears in letter source: ${label}`}
    >
      ✦ {label}
    </span>
  );
}

// ─── Anchor Name Badge ────────────────────────────────────────────────────────

function AnchorBadge() {
  return (
    <span
      className="font-mono text-[8px] px-1.5 py-0.5 rounded-sm"
      style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)', color: '#c9a84c', letterSpacing: '.08em' }}
      title="Anchor Name — foundational to the framework"
    >
      ⬡ Anchor
    </span>
  );
}

// ─── Name Card ────────────────────────────────────────────────────────────────

interface NameCardProps {
  name: NameEntry;
  isExpanded: boolean;
  onToggle: () => void;
  activeSeqFilter: string;
  activeKeyFilter: string;
  onScrollToName?: (id: string) => void;
}

function NameCard({ name, isExpanded, onToggle, activeSeqFilter, activeKeyFilter, onScrollToName }: NameCardProps) {
  const [activeTab, setActiveTab] = useState<'sequences' | 'keys' | 'sunnat' | 'quran'>('sequences');
  const [flashing, setFlashing] = useState(false);

  const filteredSequences = useMemo(() => {
    if (activeSeqFilter === 'all') return name.sequences;
    return name.sequences.filter(s => s.seqId === activeSeqFilter);
  }, [name.sequences, activeSeqFilter]);

  const filteredKeyConnections = useMemo(() => {
    if (activeKeyFilter === 'all') return name.keyConnections;
    return name.keyConnections.filter(k => k.keyId === activeKeyFilter);
  }, [name.keyConnections, activeKeyFilter]);

  const handleToggle = () => {
    setFlashing(true);
    setTimeout(() => setFlashing(false), 600);
    onToggle();
  };

  const isHighlighted =
    (activeSeqFilter !== 'all' && name.sequences.some(s => s.seqId === activeSeqFilter)) ||
    (activeKeyFilter !== 'all' && name.keyConnections.some(k => k.keyId === activeKeyFilter));

  const isDimmed =
    (activeSeqFilter !== 'all' && !name.sequences.some(s => s.seqId === activeSeqFilter)) ||
    (activeKeyFilter !== 'all' && !name.keyConnections.some(k => k.keyId === activeKeyFilter));

  return (
    <div
      id={`name-${name.id}`}
      className="transition-all duration-300"
      style={{
        border: flashing
          ? '1px solid rgba(179,138,42,0.9)'
          : name.isAnchor && isExpanded
          ? '1px solid rgba(201,168,76,0.6)'
          : isExpanded
          ? '1px solid rgba(179,138,42,0.4)'
          : isHighlighted
          ? '1px solid rgba(139,105,20,0.3)'
          : isDimmed
          ? '1px solid rgba(44,74,62,0.15)'
          : '1px solid rgba(44,74,62,0.25)',
        background: flashing
          ? 'rgba(179,138,42,0.12)'
          : name.isAnchor && isExpanded
          ? 'rgba(201,168,76,0.06)'
          : isExpanded
          ? 'rgba(28,43,37,0.5)'
          : isHighlighted
          ? 'rgba(44,74,62,0.3)'
          : isDimmed
          ? 'rgba(28,43,37,0.15)'
          : 'rgba(28,43,37,0.25)',
        opacity: isDimmed ? 0.45 : 1,
        transition: flashing ? 'background 0.15s ease, border-color 0.15s ease' : 'all 0.3s',
        boxShadow: name.isAnchor && isExpanded ? '0 0 20px rgba(201,168,76,0.08)' : 'none',
      }}
    >
      {/* Card Header */}
      <button
        onClick={handleToggle}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-white/3 transition-colors"
      >
        {/* Arabic */}
        <span
          className={`text-2xl flex-shrink-0 transition-colors ${isExpanded ? 'text-white/60' : 'text-white/20'}`}
          style={{ fontFamily: 'serif', minWidth: '2.5rem', textAlign: 'right' }}
        >
          {name.ar}
        </span>

        {/* Name info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-mono text-xs font-black uppercase tracking-wide ${isExpanded ? 'text-white' : 'text-white/60'}`}>
              {name.transliteration}
            </span>
            <FrequencyBadge count={name.frequency} />
            <span className={`font-mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 border ${
              isExpanded ? 'border-white/20 text-white/40' : 'border-white/10 text-white/25'
            }`}>
              {name.category}
            </span>
            {name.isAnchor && <AnchorBadge />}
            {name.letterSource && <LetterSourceBadge source={name.letterSource} />}
          </div>
          <p className="font-mono text-[10px] text-white/35 mt-0.5">{name.meaning}</p>
          {/* Paired Name indicator */}
          {name.pairedWith && (
            <div className="flex items-center gap-1 mt-1">
              <span className="font-mono text-[8px] text-white/20 uppercase tracking-widest">Paired with:</span>
              <button
                onClick={(e) => { e.stopPropagation(); onScrollToName?.(name.pairedWith!); }}
                className="font-mono text-[8px] uppercase tracking-widest transition-colors"
                style={{ color: 'rgba(45,138,135,0.7)', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
              >
                {NAMES_DATA.find(n => n.id === name.pairedWith)?.transliteration || name.pairedWith} ↔
              </button>
            </div>
          )}
        </div>

        {/* Sequence count dots */}
        <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
          {name.sequences.slice(0, 8).map((seq, i) => (
            <div
              key={i}
              className={`w-1.5 h-1.5 rounded-full transition-colors ${
                activeSeqFilter !== 'all' && seq.seqId === activeSeqFilter ?'bg-white' :'bg-white/20'
              }`}
              title={seq.seqTitle}
            />
          ))}
          {name.sequences.length > 8 && (
            <span className="font-mono text-[8px] text-white/20">+{name.sequences.length - 8}</span>
          )}
        </div>

        <BookmarkButton
          item={{
            item_type: 'names_entry',
            item_id: name.id,
            item_title: name.transliteration,
            item_arabic: name.ar,
            item_href: `/names-reference#${name.id}`,
            item_description: name.meaning,
            item_category: name.category,
          }}
          size="sm"
          className="flex-shrink-0"
        />

        <span className="font-mono text-sm text-white/20 flex-shrink-0 ml-2">
          {isExpanded ? '−' : '+'}
        </span>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-white/10">
          {/* Anchor Name special header */}
          {name.isAnchor && (
            <div className="px-4 py-3 border-b" style={{ background: 'rgba(201,168,76,0.04)', borderColor: 'rgba(201,168,76,0.15)' }}>
              <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(201,168,76,0.5)' }}>
                ⬡ Anchor Name — Foundational to the Framework
              </p>
              <p className="font-mono text-[10px] leading-relaxed" style={{ color: 'rgba(245,240,232,0.4)' }}>
                {name.id === 'ahad' && 'Al-Ahad is the uncaused cause of all attributes — every Name in this reference is an attribute of The One. The IRL Lens culminates in recognition of Al-Ahad.'}
                {name.id === 'hadi' && 'Al-Hadi is the most comprehensive Name for the guidance we seek — every Ayah is a form of guidance. The IRL Lens is a tool for receiving Al-Hadi\'s guidance through creation.'}
                {name.id === 'badi' && 'Al-Badi is the Originator of all essential natures — the source of every language and concept. Every category of being originates from Al-Badi before any instance of it existed.'}
              </p>
            </div>
          )}

          {/* Fitra Note — "You already know this Name" */}
          <div className="px-4 py-3 border-b" style={{ borderColor: 'rgba(201,168,76,0.08)' }}>
            <NamesReferenceFitraNote nameName={name.transliteration} />
          </div>

          {/* Paired Name cross-link */}
          {name.pairedWith && (
            <div className="px-4 py-2 border-b flex items-center gap-3" style={{ background: 'rgba(45,138,135,0.04)', borderColor: 'rgba(45,138,135,0.15)' }}>
              <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(45,138,135,0.6)' }}>Paired Name:</span>
              <button
                onClick={() => onScrollToName?.(name.pairedWith!)}
                className="font-mono text-[9px] uppercase tracking-widest transition-colors"
                style={{ color: 'rgba(45,138,135,0.8)', border: '1px solid rgba(45,138,135,0.3)', padding: '2px 8px', borderRadius: 3 }}
              >
                {NAMES_DATA.find(n => n.id === name.pairedWith)?.transliteration} — {NAMES_DATA.find(n => n.id === name.pairedWith)?.meaning} →
              </button>
              <span className="font-mono text-[8px]" style={{ color: 'rgba(245,240,232,0.25)' }}>
                {name.id === 'zahir' && 'Az-Zahir ↔ Al-Batin: the primary axis of the 8-layer depth panel'}
                {name.id === 'batin' && 'Al-Batin ↔ Az-Zahir: the primary axis of the 8-layer depth panel'}
              </span>
            </div>
          )}

          {/* Root info */}
          <div className="px-4 py-3 bg-white/2 border-b border-white/8 flex flex-wrap gap-4">
            <div>
              <span className="font-mono text-[9px] text-white/20 uppercase tracking-widest">Root</span>
              <p className="font-mono text-xs text-white/50 mt-0.5">{name.root} — {name.rootMeaning}</p>
            </div>
            <div>
              <span className="font-mono text-[9px] text-white/20 uppercase tracking-widest">Appears in</span>
              <p className="font-mono text-xs text-white/50 mt-0.5">{name.frequency} sequences</p>
            </div>
            {name.letterSource && (
              <div>
                <span className="font-mono text-[9px] text-white/20 uppercase tracking-widest">Letter Source</span>
                <p className="font-mono text-xs text-white/50 mt-0.5">{name.letterSource === 'hadith-99' ? '99 Names Hadith' : `Quran ${name.letterSource}`}</p>
              </div>
            )}
          </div>

          {/* Tab navigation */}
          <div className="flex border-b border-white/10 overflow-x-auto">
            {(['sequences', 'keys', 'sunnat', 'quran'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`font-mono text-[9px] tracking-widest uppercase px-4 py-2.5 whitespace-nowrap transition-all border-r border-white/8 last:border-r-0 ${
                  activeTab === tab ? 'bg-white/8 text-white' : 'text-white/30 hover:text-white/50'
                }`}
              >
                {tab === 'sequences' ? `Sequences (${filteredSequences.length})` :
                 tab === 'keys' ? `Keys (${filteredKeyConnections.length})` :
                 tab === 'sunnat' ? `Sunnat Steps (${name.sunnatSteps.length})` :
                 `Quranic Refs (${name.quranicRefs.length})`}
              </button>
            ))}
          </div>

          <div className="p-4">
            {/* Sequences tab */}
            {activeTab === 'sequences' && (
              <div className="flex flex-col gap-2">
                {filteredSequences.length === 0 ? (
                  <p className="font-mono text-xs text-white/25 italic">No sequences match the current filter.</p>
                ) : (
                  filteredSequences.map((seq) => (
                    <div
                      key={seq.seqId}
                      className={`border p-3 ${
                        activeSeqFilter !== 'all' && seq.seqId === activeSeqFilter ?'border-white/30 bg-white/5' :'border-white/8'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div>
                          <span className="font-mono text-[9px] text-white/25 uppercase tracking-widest">
                            S{String(seq.seqNum).padStart(2, '0')} · {seq.city} · {seq.biome}
                          </span>
                          <p className="font-mono text-[10px] font-black text-white/60 uppercase tracking-wide mt-0.5">
                            {seq.seqTitle}
                          </p>
                        </div>
                        <span className={`font-mono text-[8px] tracking-widest uppercase px-1.5 py-0.5 border flex-shrink-0 ${
                          seq.layer === 'names' ? 'border-white/20 text-white/40' :
                          seq.layer === 'sunnat'? 'border-white/15 text-white/30' : 'border-white/10 text-white/20'
                        }`}>
                          {seq.layer}
                        </span>
                      </div>
                      <p className="font-mono text-[10px] text-white/40 leading-relaxed">{seq.role}</p>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Keys tab */}
            {activeTab === 'keys' && (
              <div className="flex flex-col gap-3">
                {filteredKeyConnections.length === 0 ? (
                  <p className="font-mono text-xs text-white/25 italic">No key connections match the current filter.</p>
                ) : (
                  filteredKeyConnections.map((kc) => (
                    <div key={kc.keyId} className="border border-white/8 p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-mono text-[9px] text-white/25 uppercase tracking-widest">Key {kc.keyNum}</span>
                        <span className="font-mono text-[10px] font-black text-white/60 uppercase tracking-wide">{kc.keyId}</span>
                      </div>
                      <p className="font-mono text-[10px] text-white/40 leading-relaxed">{kc.connection}</p>
                    </div>
                  ))
                )}
                {name.keyConnections.length === 0 && (
                  <p className="font-mono text-xs text-white/25 italic">This Name appears in sequences but has not yet been mapped to a specific Key connection.</p>
                )}
              </div>
            )}

            {/* Sunnat Steps tab */}
            {activeTab === 'sunnat' && (
              <div className="flex flex-col gap-2">
                {name.sunnatSteps.length === 0 ? (
                  <p className="font-mono text-xs text-white/25 italic">This Name operates across sequences but is not named in a specific Sunnat step. See Sequences tab for context.</p>
                ) : (
                  name.sunnatSteps.map((step, i) => (
                    <div key={i} className="border border-white/8 p-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="font-mono text-[9px] text-white/20 uppercase tracking-widest">Step {step.step}</span>
                        <span className="font-mono text-[10px] font-black text-white/50 uppercase tracking-wide">{step.seqTitle}</span>
                      </div>
                      <p className="font-mono text-[10px] text-white/40 leading-relaxed">{step.description}</p>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Quranic Refs tab */}
            {activeTab === 'quran' && (
              <div className="flex flex-col gap-3">
                {name.quranicRefs.map((ref, i) => (
                  <div key={i} className="border-l-2 border-white/15 pl-3">
                    <p className="font-mono text-[9px] text-white/25 uppercase tracking-widest mb-1">{ref.ref}</p>
                    <p className="font-mono text-xs text-white/50 leading-relaxed italic">{ref.text}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Cross-Name connections */}
            {name.crossNameConnections.length > 0 && (
              <div className="mt-4 pt-3 border-t border-white/8">
                <p className="font-mono text-[9px] text-white/20 uppercase tracking-widest mb-2">Frequently Appears With</p>
                <div className="flex flex-wrap gap-1.5">
                  {name.crossNameConnections.map((connId) => {
                    const connName = NAMES_DATA.find(n => n.id === connId);
                    return connName ? (
                      <button
                        key={connId}
                        onClick={() => onScrollToName?.(connId)}
                        className="font-mono text-[9px] text-left transition-colors"
                        style={{ color: 'rgba(245,240,232,0.3)' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.8)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.3)'; }}
                      >
                        → {connName.transliteration}
                      </button>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Field prompt */}
            <div className="mt-4 pt-3 border-t border-white/8 bg-white/2 p-3">
              <p className="font-mono text-[9px] text-white/20 uppercase tracking-widest mb-1.5">Observation Prompt</p>
              <p className="font-mono text-[10px] text-white/40 leading-relaxed italic">{name.fieldPrompt}</p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ─── Sunnat Timeline Card ─────────────────────────────────────────────────────

interface TimelineCardProps {
  timeline: SunnatTimeline;
  isExpanded: boolean;
  onToggle: () => void;
  activeSeqFilter: string;
}

function TimelineCard({ timeline, isExpanded, onToggle, activeSeqFilter }: TimelineCardProps) {
  const isHighlighted = activeSeqFilter === timeline.seqId;
  const isDimmed = activeSeqFilter !== 'all' && activeSeqFilter !== timeline.seqId;

  return (
    <div
      className={`border transition-all duration-300 ${
        isExpanded
          ? 'border-white/40'
          : isHighlighted
          ? 'border-white/30 bg-white/3'
          : isDimmed
          ? 'border-white/5 opacity-40' :'border-white/10 hover:border-white/25'
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-white/3 transition-colors"
      >
        <div className="flex-shrink-0 w-8 h-8 border border-white/15 flex items-center justify-center">
          <span className="font-mono text-[9px] text-white/30">S{String(SEQUENCE_REGISTRY[timeline.seqId]?.num ?? 0).padStart(2, '0')}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-mono text-xs font-black uppercase tracking-wide ${isExpanded ? 'text-white' : 'text-white/60'}`}>
            {timeline.seqTitle}
          </p>
          <p className="font-mono text-[9px] text-white/25 mt-0.5">{timeline.city} · {timeline.biome}</p>
        </div>
        <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
          {timeline.steps.map((_, i) => (
            <div key={i} className="w-1.5 h-1.5 rounded-full bg-white/15" />
          ))}
          <span className="font-mono text-[8px] text-white/20 ml-1">{timeline.steps.length} steps</span>
        </div>
        <span className="font-mono text-sm text-white/20 flex-shrink-0 ml-2">{isExpanded ? '−' : '+'}</span>
      </button>

      {isExpanded && (
        <div className="border-t border-white/10">
          {/* Ayah */}
          <div className="px-4 py-3 bg-white/2 border-b border-white/8">
            <p className="font-mono text-[9px] text-white/20 uppercase tracking-widest mb-1">Ayah</p>
            <p className="font-mono text-xs text-white/45 leading-relaxed italic">{timeline.ayah}</p>
          </div>

          {/* Sequential steps */}
          <div className="p-4">
            <div className="relative">
              {/* Vertical connector line */}
              <div className="absolute left-4 top-6 bottom-6 w-px bg-white/8" />

              <div className="flex flex-col gap-0">
                {timeline.steps.map((step, i) => (
                  <div key={i} className="relative flex gap-4 pb-4 last:pb-0">
                    {/* Step number node */}
                    <div className="flex-shrink-0 w-8 h-8 border border-white/20 bg-black flex items-center justify-center z-10">
                      <span className="font-mono text-[9px] text-white/40">{step.step}</span>
                    </div>

                    {/* Step content */}
                    <div className="flex-1 min-w-0 pt-1">
                      <p className="font-mono text-xs text-white/60 font-black leading-tight mb-1">{step.description}</p>

                      {step.amr && (
                        <p className="font-mono text-[9px] text-white/30 mb-1.5 italic">
                          Amr: <span className="text-white/50">{step.amr}</span>
                        </p>
                      )}

                      {step.note && (
                        <p className="font-mono text-[10px] text-white/35 leading-relaxed mb-2">{step.note}</p>
                      )}

                      {/* Active Names */}
                      <div className="flex flex-wrap gap-1">
                        {step.namesActive.map((nameId) => {
                          const nameData = NAMES_DATA.find(n => n.transliteration === nameId || n.id === nameId.toLowerCase().replace(/[^a-z]/g, '').replace('al', '').replace('ar', '').replace('as', '').replace('an', '').replace('ash', ''));
                          return (
                            <span
                              key={nameId}
                              className="font-mono text-[8px] tracking-widest uppercase text-white/40 border border-white/12 px-1.5 py-0.5"
                            >
                              {nameId}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Frequency Cluster View ───────────────────────────────────────────────────

function FrequencyClusterView({ names, onNameClick }: { names: NameEntry[]; onNameClick: (id: string) => void }) {
  const clusters = useMemo(() => {
    const high = names.filter(n => n.frequency >= 10);
    const medium = names.filter(n => n.frequency >= 7 && n.frequency < 10);
    const regular = names.filter(n => n.frequency >= 4 && n.frequency < 7);
    const occasional = names.filter(n => n.frequency < 4);
    return { high, medium, regular, occasional };
  }, [names]);

  const ClusterGroup = ({ label, items, size }: { label: string; items: NameEntry[]; size: 'lg' | 'md' | 'sm' | 'xs' }) => {
    if (items.length === 0) return null;
    const textSize = size === 'lg' ? 'text-sm' : size === 'md' ? 'text-xs' : size === 'sm' ? 'text-[10px]' : 'text-[9px]';
    const arSize = size === 'lg' ? 'text-2xl' : size === 'md' ? 'text-xl' : size === 'sm' ? 'text-lg' : 'text-base';
    return (
      <div className="mb-6">
        <p className="font-mono text-[9px] text-white/20 uppercase tracking-widest mb-3">{label}</p>
        <div className="flex flex-wrap gap-2">
          {items.map(n => (
            <button
              key={n.id}
              onClick={() => onNameClick(n.id)}
              className="flex flex-col items-center gap-1 border border-white/10 hover:border-white/30 p-2 transition-all group"
            >
              <span className={`${arSize} text-white/20 group-hover:text-white/40 transition-colors`} style={{ fontFamily: 'serif' }}>
                {n.ar}
              </span>
              <span className={`font-mono ${textSize} text-white/40 group-hover:text-white/70 transition-colors uppercase tracking-wide`}>
                {n.transliteration}
              </span>
              <FrequencyBadge count={n.frequency} />
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <ClusterGroup label={`High Frequency (10+ sequences) — ${clusters.high.length} Names`} items={clusters.high} size="lg" />
      <ClusterGroup label={`Medium Frequency (7–9 sequences) — ${clusters.medium.length} Names`} items={clusters.medium} size="md" />
      <ClusterGroup label={`Regular Frequency (4–6 sequences) — ${clusters.regular.length} Names`} items={clusters.regular} size="sm" />
      <ClusterGroup label={`Occasional (1–3 sequences) — ${clusters.occasional.length} Names`} items={clusters.occasional} size="xs" />
    </div>
  );
}

// ─── Main Page (inner, reads search params) ───────────────────────────────────

function NamesReferenceInner() {
  const searchParams = useSearchParams();
  const nameParam = searchParams.get('name');

  const [view, setView] = useState<'names' | 'timelines' | 'clusters'>('names');
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [activeKeyFilter, setActiveKeyFilter] = useState<string>('all');
  const [activeSeqFilter, setActiveSeqFilter] = useState<string>('all');
  const [expandedNames, setExpandedNames] = useState<Set<string>>(new Set());
  const [expandedTimelines, setExpandedTimelines] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // NameBridge: auto-expand and scroll to name from URL param
  useEffect(() => {
    if (nameParam) {
      const nameEntry = NAMES_DATA.find(n => n.id === nameParam);
      if (nameEntry) {
        setView('names');
        setActiveCategory('All');
        setActiveKeyFilter('all');
        setActiveSeqFilter('all');
        setSearchQuery('');
        setExpandedNames(prev => new Set([...prev, nameParam]));
        // Scroll after state settles
        setTimeout(() => {
          const el = document.getElementById(`name-${nameParam}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Flash highlight
            el.style.transition = 'box-shadow 0.4s';
            el.style.boxShadow = '0 0 0 2px rgba(201,168,76,0.6)';
            setTimeout(() => { el.style.boxShadow = ''; }, 1800);
          }
        }, 200);
      }
    }
  }, [nameParam]);

  const filteredNames = useMemo(() => {
    return NAMES_DATA.filter(name => {
      const matchesCategory = activeCategory === 'All' || name.category === activeCategory;
      const matchesKey = activeKeyFilter === 'all' || name.keyConnections.some(k => k.keyId === activeKeyFilter);
      const matchesSeq = activeSeqFilter === 'all' || name.sequences.some(s => s.seqId === activeSeqFilter);
      const matchesSearch = searchQuery === '' ||
        name.transliteration.toLowerCase().includes(searchQuery.toLowerCase()) ||
        name.meaning.toLowerCase().includes(searchQuery.toLowerCase()) ||
        name.ar.includes(searchQuery);
      return matchesCategory && matchesKey && matchesSeq && matchesSearch;
    }).sort((a, b) => b.frequency - a.frequency);
  }, [activeCategory, activeKeyFilter, activeSeqFilter, searchQuery]);

  const filteredTimelines = useMemo(() => {
    if (activeSeqFilter === 'all') return SUNNAT_TIMELINES;
    return SUNNAT_TIMELINES.filter(t => t.seqId === activeSeqFilter);
  }, [activeSeqFilter]);

  const toggleName = (id: string) => {
    setExpandedNames(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleTimeline = (id: string) => {
    setExpandedTimelines(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const scrollToName = (id: string) => {
    setView('names');
    setExpandedNames(prev => new Set([...prev, id]));
    setTimeout(() => {
      document.getElementById(`name-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // Stats
  const totalNames = NAMES_DATA.length;
  const totalSequences = Object.keys(SEQUENCE_REGISTRY).length;
  const totalTimelines = SUNNAT_TIMELINES.length;
  const totalSteps = SUNNAT_TIMELINES.reduce((acc, t) => acc + t.steps.length, 0);

  return (
    <div className="min-h-screen deep-green-bg">
      <Header />
      <Breadcrumb
        items={[
          { label: 'Open Letter', href: '/open-letter' },
          { label: 'Keys to Kulli Haal', href: '/keys-to-kulli-haal' },
          { label: 'Names Reference' },
        ]}
        showReturnToLetter={true}
      />
      <JourneyNav currentPage="keys-to-kulli-haal" />

      <main>
        {/* Hero — with parallax background */}
        <section className="pt-24 pb-12 relative overflow-hidden" style={{ borderBottom: '1px solid rgba(139,105,20,0.2)' }}>
          {/* Parallax background layer */}
          <NamesParallaxBg />
          <div className="max-w-[1280px] mx-auto px-6 lg:px-8 relative" style={{ zIndex: 2 }}>
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
              <ScrollReveal direction="up" delay={0}>
              <div>
                <p className="font-mono text-[10px] tracking-widest uppercase mb-3" style={{ color: 'rgba(139,105,20,0.6)' }}>
                  Keys to Kulli Haal · Names Reference
                </p>
                <h1 className="font-mono text-3xl lg:text-4xl font-black uppercase tracking-tight mb-3" style={{ color: '#F5F0E8' }}>
                  الْأَسْمَاء الْحُسْنَى
                </h1>
                <p className="font-mono text-sm max-w-2xl leading-relaxed" style={{ color: 'rgba(245,240,232,0.45)' }}>
                  All Names appearing across the IRL Lens sequences — organized by frequency, linked to sequences, 
                  Quranic references, and cross-Key connections. Each Name is a vector of recognition: 
                  a way of reading creation that the IRL Lens makes methodological.
                </p>
              </div>
              </ScrollReveal>
              <div className="flex flex-wrap gap-4 lg:flex-shrink-0">
                {[
                  { label: 'Names', value: totalNames },
                  { label: 'Sequences', value: totalSequences },
                  { label: 'Timelines', value: totalTimelines },
                  { label: 'Sunnat Steps', value: totalSteps },
                ].map(stat => (
                  <div key={stat.label} className="px-4 py-3 text-center" style={{ border: '1px solid rgba(139,105,20,0.25)', background: 'rgba(28,43,62,0.4)' }}>
                    <p className="font-mono text-2xl font-black" style={{ color: '#D4A82A' }}>{stat.value}</p>
                    <p className="font-mono text-[9px] uppercase tracking-widest mt-0.5" style={{ color: 'rgba(245,240,232,0.3)' }}>{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* NameBridge indicator — shown when arriving via name link */}
            {nameParam && (
              <div className="mt-6 p-3 flex items-center gap-3" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 4 }}>
                <span style={{ fontSize: 14, color: '#c9a84c' }}>↗</span>
                <p className="font-mono text-[10px]" style={{ color: 'rgba(201,168,76,0.8)' }}>
                  NameBridge: Navigated from another section of the platform to <strong style={{ color: '#c9a84c' }}>{nameParam}</strong>. The entry is expanded below.
                </p>
                <Link href="/open-letter" className="font-mono text-[9px] tracking-widest uppercase ml-auto" style={{ color: 'rgba(201,168,76,0.5)', whiteSpace: 'nowrap' }}>
                  ← Back to Letter
                </Link>
              </div>
            )}

            {/* Platform position note */}
            <div className="mt-8 p-4" style={{ border: '1px solid rgba(44,74,62,0.4)', background: 'rgba(28,43,62,0.35)' }}>
              <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: 'rgba(139,105,20,0.5)' }}>Platform Position</p>
              <p className="font-mono text-xs leading-relaxed" style={{ color: 'rgba(245,240,232,0.35)' }}>
                Keys to Kulli Haal (SL) → <span style={{ color: 'rgba(196,149,106,0.7)' }}>IRL Lens</span> → Muslim Resource Guide (A) · 
                Beneath the IRL Lens: ED (Ayat in Creation) and O (Order of Operations / Sunnat Allah). 
                This reference maps the Names that compose the O layer — the order of operations through which 
                Names and Attributes are woven into recognizable patterns.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Link href="/keys-to-kulli-haal" className="font-mono text-[9px] tracking-widest uppercase px-3 py-1.5 transition-all"
                  style={{ color: 'rgba(245,240,232,0.4)', border: '1px solid rgba(44,74,62,0.4)' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(139,105,20,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.8)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(44,74,62,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.4)'; }}
                >
                  Keys to Kulli Haal →
                </Link>
                <Link href="/inner-reality-lens" className="font-mono text-[9px] tracking-widest uppercase px-3 py-1.5 transition-all"
                  style={{ color: 'rgba(245,240,232,0.4)', border: '1px solid rgba(44,74,62,0.4)' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(139,105,20,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.8)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(44,74,62,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.4)'; }}
                >
                  IRL Lens →
                </Link>
                <Link href="/muslim-resource-guide" className="font-mono text-[9px] tracking-widest uppercase px-3 py-1.5 transition-all"
                  style={{ color: 'rgba(245,240,232,0.4)', border: '1px solid rgba(44,74,62,0.4)' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(139,105,20,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.8)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(44,74,62,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.4)'; }}
                >
                  Muslim Resource Guide →
                </Link>
                <Link href="/open-letter" className="font-mono text-[9px] tracking-widest uppercase px-3 py-1.5 transition-all"
                  style={{ color: 'rgba(245,240,232,0.4)', border: '1px solid rgba(44,74,62,0.4)' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(139,105,20,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.8)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(44,74,62,0.4)'; (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.4)'; }}
                >
                  Open Letter →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Controls */}
        <section className="sticky top-[88px] z-30 border-b" style={{ background: 'rgba(28,43,62,0.97)', borderColor: 'rgba(139,105,20,0.2)', backdropFilter: 'blur(16px)' }}>
          <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-3">
            <div className="flex flex-col gap-3">
              {/* View toggle + Search */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex gap-0" style={{ border: '1px solid rgba(44,74,62,0.4)' }}>
                  {(['names', 'timelines', 'clusters'] as const).map(v => (
                    <button
                      key={v}
                      onClick={() => setView(v)}
                      className="font-mono text-[9px] tracking-widest uppercase px-4 py-2 transition-all border-r last:border-r-0"
                      style={{
                        borderColor: 'rgba(44,74,62,0.4)',
                        background: view === v ? 'rgba(139,105,20,0.9)' : 'transparent',
                        color: view === v ? '#F5F0E8' : 'rgba(245,240,232,0.3)',
                      }}
                    >
                      {v === 'names' ? `Names (${filteredNames.length})` :
                       v === 'timelines' ? `Timelines (${filteredTimelines.length})` :
                       'Frequency Map'}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Search names..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="font-mono text-xs px-3 py-2 focus:outline-none w-48"
                  style={{ background: 'rgba(28,43,62,0.6)', border: '1px solid rgba(44,74,62,0.4)', color: 'rgba(245,240,232,0.7)' }}
                />
              </div>

              {/* Filters */}
              <div className="flex flex-wrap gap-2">
                {/* Key filter */}
                <div className="flex gap-0 overflow-x-auto" style={{ border: '1px solid rgba(44,74,62,0.3)' }}>
                  {KEY_FILTERS.map(f => (
                    <button
                      key={f.id}
                      onClick={() => setActiveKeyFilter(f.id)}
                      className="font-mono text-[8px] tracking-widest uppercase px-3 py-1.5 whitespace-nowrap transition-all border-r last:border-r-0"
                      style={{
                        borderColor: 'rgba(44,74,62,0.3)',
                        background: activeKeyFilter === f.id ? 'rgba(44,74,62,0.5)' : 'transparent',
                        color: activeKeyFilter === f.id ? '#F5F0E8' : 'rgba(245,240,232,0.3)',
                      }}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>

                {/* Sequence filter */}
                <select
                  value={activeSeqFilter}
                  onChange={e => setActiveSeqFilter(e.target.value)}
                  className="font-mono text-[8px] px-3 py-1.5 focus:outline-none"
                  style={{ background: '#1C2B25', border: '1px solid rgba(44,74,62,0.4)', color: 'rgba(245,240,232,0.5)' }}
                >
                  {SEQUENCE_FILTERS.map(f => (
                    <option key={f.id} value={f.id}>{f.label}</option>
                  ))}
                </select>

                {/* Category filter */}
                <div className="flex gap-0 overflow-x-auto" style={{ border: '1px solid rgba(44,74,62,0.3)' }}>
                  {NAME_CATEGORIES.slice(0, 6).map(cat => (
                    <button
                      key={cat}
                      onClick={() => setActiveCategory(cat)}
                      className="font-mono text-[8px] tracking-widest uppercase px-3 py-1.5 whitespace-nowrap transition-all border-r last:border-r-0"
                      style={{
                        borderColor: 'rgba(44,74,62,0.3)',
                        background: activeCategory === cat ? 'rgba(44,74,62,0.5)' : 'transparent',
                        color: activeCategory === cat ? '#F5F0E8' : 'rgba(245,240,232,0.3)',
                      }}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main content */}
        <section className="py-12">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-8">

            {/* Names View */}
            {view === 'names' && (
              <div className="flex flex-col gap-2">
                {filteredNames.length === 0 ? (
                  <div className="p-8 text-center" style={{ border: '1px solid rgba(44,74,62,0.3)' }}>
                    <p className="font-mono text-xs" style={{ color: 'rgba(245,240,232,0.3)' }}>No Names match the current filters.</p>
                    <button
                      onClick={() => { setActiveCategory('All'); setActiveKeyFilter('all'); setActiveSeqFilter('all'); setSearchQuery(''); }}
                      className="font-mono text-[10px] tracking-widest uppercase text-white/35 border border-white/12 px-4 py-2 mt-4 hover:border-white/35 transition-all"
                    >
                      Clear Filters
                    </button>
                  </div>
                ) : (
                  filteredNames.map(name => (
                    <NameCard
                      key={name.id}
                      name={name}
                      isExpanded={expandedNames.has(name.id)}
                      onToggle={() => toggleName(name.id)}
                      activeSeqFilter={activeSeqFilter}
                      activeKeyFilter={activeKeyFilter}
                      onScrollToName={scrollToName}
                    />
                  ))
                )}
              </div>
            )}

            {/* Timelines View */}
            {view === 'timelines' && (
              <div>
                {/* ─── FEATURED: Leaf Sequence ─────────────────────────────── */}
                <div className="mb-8 p-5" style={{ border: '1px solid rgba(201,168,76,0.35)', background: 'rgba(28,43,62,0.5)', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, rgba(201,168,76,0.6), rgba(45,122,71,0.4), rgba(201,168,76,0.6))' }} />
                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-mono text-[9px] tracking-widest uppercase px-2 py-0.5" style={{ background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.4)', color: '#c9a84c' }}>
                      ✦ Featured Sunnat Timeline
                    </span>
                    <span className="font-mono text-[8px] tracking-widest uppercase" style={{ color: 'rgba(45,122,71,0.7)' }}>Leaf Sequence</span>
                  </div>
                  <h3 className="font-mono text-sm font-black uppercase tracking-wide mb-1" style={{ color: '#F5F0E8' }}>
                    The Leaf Sequence — تَسَلْسُلُ الْوَرَقَة
                  </h3>
                  <p className="font-mono text-[10px] mb-4 leading-relaxed" style={{ color: 'rgba(245,240,232,0.45)' }}>
                    A tutorial walkthrough of the IRL Lens methodology through six Names — the foundational recognition arc from divine power through provision to illumination. This sequence is the onboarding pathway of the platform.
                  </p>
                  {/* Six-step leaf sequence */}
                  <div className="flex flex-col gap-0 relative">
                    <div className="absolute left-4 top-5 bottom-5 w-px" style={{ background: 'rgba(201,168,76,0.15)' }} />
                    {[
                      { name: 'Al-Qadir', ar: 'الْقَادِر', meaning: 'The All-Powerful', step: 1, note: 'Recognition begins with acknowledging divine capacity — the power behind every observable process.', color: '#c9a84c' },
                      { name: 'Al-Khaliq', ar: 'الْخَالِق', meaning: 'The Creator', step: 2, note: 'From power comes creation — the origination of form from nothing. The leaf did not design itself.', color: '#c9a84c' },
                      { name: 'Al-Bari', ar: 'الْبَارِئ', meaning: 'The Evolver / Fashioner from Nothing', step: 3, note: 'The fashioning of distinct form — each leaf shaped precisely, no two identical. Al-Bari separates and distinguishes.', color: '#2d7a47' },
                      { name: 'Al-Musawwir', ar: 'الْمُصَوِّر', meaning: 'The Fashioner of Forms', step: 4, note: 'The imaging — the visible form given to the created thing. The color, the veins, the texture. Zahir made manifest.', color: '#2d7a47' },
                      { name: 'Ar-Razzaq', ar: 'الرَّزَّاق', meaning: 'The Ever Provider', step: 5, note: 'The leaf is a provision engine — photosynthesis as Ar-Razzaq\'s mechanism. Rizq flows through the chlorophyll.', color: '#2d8a87' },
                      { name: 'An-Nur', ar: 'النُّور', meaning: 'The Light', step: 6, note: 'The sequence closes with light — the leaf is a receiver of An-Nur, converting divine light into sustenance. Alhamdulillah.', color: '#c9a84c' },
                    ].map((item, i) => (
                      <div key={item.name} className="flex gap-4 pb-4 last:pb-0 relative">
                        <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center z-10" style={{ background: '#0a1a12', border: `1px solid ${item.color}40` }}>
                          <span className="font-mono text-[9px]" style={{ color: item.color }}>{item.step}</span>
                        </div>
                        <div className="flex-1 pt-1">
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <span className="font-mono text-xs font-black uppercase tracking-wide" style={{ color: '#F5F0E8' }}>{item.name}</span>
                            <span className="text-base" style={{ fontFamily: 'serif', color: item.color }}>{item.ar}</span>
                            <span className="font-mono text-[9px]" style={{ color: 'rgba(245,240,232,0.35)' }}>{item.meaning}</span>
                          </div>
                          <p className="font-mono text-[10px] text-white/40 leading-relaxed" style={{ color: 'rgba(245,240,232,0.4)' }}>{item.note}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 flex items-center gap-2" style={{ borderTop: '1px solid rgba(201,168,76,0.1)' }}>
                    <span className="font-mono text-[9px] tracking-widest uppercase" style={{ color: 'rgba(201,168,76,0.5)' }}>Closing State:</span>
                    <span className="font-mono text-[10px]" style={{ color: '#c9a84c' }}>الْحَمْدُ لِلَّه — Alhamdulillah</span>
                    <span className="font-mono text-[9px]" style={{ color: 'rgba(245,240,232,0.3)' }}>— every recognition arc returns to gratitude</span>
                  </div>
                </div>

                <div className="mb-6 border border-white/8 p-4 bg-white/2">
                  <p className="font-mono text-[9px] text-white/20 uppercase tracking-widest mb-2">About Sunnat Timelines</p>
                  <p className="font-mono text-xs text-white/35 leading-relaxed">
                    Each timeline is a visual sequence showing the Order of Operations (Sunnat Allah) for a specific 
                    ecological encounter. Each step is a divine command (Amr) executing, with the Names active at 
                    each stage. The sequence cannot be shortcut — the order IS the process. The process IS the way 
                    Allah does this particular thing.
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  {filteredTimelines.map(timeline => (
                    <TimelineCard
                      key={timeline.seqId}
                      timeline={timeline}
                      isExpanded={expandedTimelines.has(timeline.seqId)}
                      onToggle={() => toggleTimeline(timeline.seqId)}
                      activeSeqFilter={activeSeqFilter}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Frequency Cluster View */}
            {view === 'clusters' && (
              <div>
                <div className="mb-6 border border-white/8 p-4 bg-white/2">
                  <p className="font-mono text-[9px] text-white/20 uppercase tracking-widest mb-2">Frequency Map</p>
                  <p className="font-mono text-xs max-w-2xl leading-relaxed" style={{ color: 'rgba(245,240,232,0.35)' }}>
                    Names clustered by frequency of appearance across all sequences. High-frequency Names are the 
                    structural Names of the IRL Lens — they appear in almost every encounter because they govern 
                    the fundamental processes of creation (provision, guidance, wisdom, creation itself). 
                    Click any Name to see its full context.
                  </p>
                </div>
                <FrequencyClusterView names={NAMES_DATA} onNameClick={scrollToName} />
              </div>
            )}
          </div>
        </section>

        {/* ─── SUNNAT SEQUENCE BUILDER ─────────────────────────────────── */}
        <SunnatSequenceBuilder />

        {/* Cross-key connections summary */}
        <section className="py-12" style={{ borderTop: '1px solid rgba(139,105,20,0.15)' }}>
          <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
            <div className="mb-8">
              <p className="font-mono text-[10px] tracking-widest uppercase mb-2" style={{ color: 'rgba(139,105,20,0.5)' }}>Cross-Key Connections</p>
              <p className="font-mono text-xs max-w-2xl leading-relaxed" style={{ color: 'rgba(245,240,232,0.35)' }}>
                How Names connect across the Five Keys — each Key activates a different dimension of the same Names.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
              {[
                { key: 'AYAT', num: '01', ar: 'آيات', names: ['Al-Basir', 'Az-Zahir', 'Al-Batin', 'Al-Haqq', 'Al-Khaliq', 'Al-Bari', 'Al-Musawwir', 'Ar-Razzaq', 'An-Nur', 'Al-Qadir'] },
                { key: 'HAAL', num: '02', ar: 'حال', names: ['Al-Khabir', 'Al-Alim', 'Al-Hayy', 'Al-Latif', 'Al-Muhawwil', 'Al-Bari', 'Al-Fattah', 'Al-Jami'] },
                { key: 'TAQWAH', num: '03', ar: 'تقوى', names: ['Al-Muhaymin', 'Al-Hakeem', 'Ar-Raqib', 'Al-Wadud', 'As-Sabur', 'Al-Hadi', 'Al-Wahhab'] },
                { key: 'MIZAN', num: '04', ar: 'مِيزَان', names: ['Al-Adl', 'Al-Haqq', 'Al-Hafiz', 'Al-Muqsit', 'Ar-Razzaq', 'Al-Hakeem', 'Al-Batin'] },
                { key: 'AMANAH', num: '05', ar: 'أمانة', names: ['Al-Hafiz', 'Al-Qadir', 'Al-Wakil', 'Ar-Rahman', 'Al-Jami', 'Al-Wadud', 'Al-Mu\'id', 'Al-Hadi'] },
              ].map(keyData => (
                <div key={keyData.key} className="p-4" style={{ border: '1px solid rgba(44,74,62,0.35)', background: 'rgba(28,43,62,0.3)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: 'rgba(139,105,20,0.5)' }}>Key {keyData.num}</p>
                      <p className="font-mono text-xs font-black uppercase tracking-wide" style={{ color: 'rgba(245,240,232,0.6)' }}>{keyData.key}</p>
                    </div>
                    <span className="text-xl" style={{ fontFamily: 'serif', color: 'rgba(179,138,42,0.25)' }}>{keyData.ar}</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    {keyData.names.map(name => (
                      <button
                        key={name}
                        onClick={() => {
                          const nameEntry = NAMES_DATA.find(n => n.transliteration === name);
                          if (nameEntry) scrollToName(nameEntry.id);
                        }}
                        className="font-mono text-[9px] text-left transition-colors"
                        style={{ color: 'rgba(245,240,232,0.3)' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(196,149,106,0.8)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(245,240,232,0.3)'; }}
                      >
                        → {name}
                      </button>
                    ))}
                  </div>
                  <Link
                    href="/keys-to-kulli-haal"
                    className="mt-3 block font-mono text-[8px] tracking-widest uppercase transition-colors"
                    style={{ color: 'rgba(139,105,20,0.5)' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(179,138,42,0.8)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(139,105,20,0.5)'; }}
                  >
                    See Key {keyData.num} →
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── QURAN 59:23-24 — ORIGIN VERSE ──────────────────────────────── */}
        <section className="py-12" style={{ borderTop: '1px solid rgba(201,168,76,0.15)' }}>
          <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-[9px] tracking-widest uppercase px-2 py-0.5" style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)', color: '#c9a84c' }}>
                ✦ Origin Verse
              </span>
              <span className="font-mono text-[8px] tracking-widest uppercase" style={{ color: 'rgba(201,168,76,0.5)' }}>The Most Concentrated Names Passage</span>
            </div>
            <div className="p-6 relative overflow-hidden" style={{ border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(28,43,62,0.5)', borderRadius: 4 }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, rgba(201,168,76,0.6), rgba(45,122,71,0.3), rgba(201,168,76,0.6))' }} />
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="flex-1">
                  <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(201,168,76,0.5)' }}>Quran 59:23–24 — Surah Al-Hashr</p>
                  <div className="mb-4 p-4" style={{ background: 'rgba(6,13,18,0.4)', border: '1px solid rgba(201,168,76,0.12)', borderRadius: 4 }}>
                    <p className="font-mono text-xs leading-relaxed mb-3" style={{ color: 'rgba(245,240,232,0.6)' }}>
                      <span style={{ color: 'rgba(201,168,76,0.5)', fontSize: 9 }}>59:23 — </span>
                      He is Allah, other than whom there is no deity, the Sovereign (Al-Malik), the Pure (Al-Quddus), the Perfection (As-Salam), the Bestower of Faith (Al-Mumin), the Guardian (Al-Muhaymin), the Exalted in Might (Al-Aziz), the Compeller (Al-Jabbar), the Superior (Al-Mutakabbir). Exalted is Allah above whatever they associate with Him.
                    </p>
                    <p className="font-mono text-xs leading-relaxed" style={{ color: 'rgba(245,240,232,0.6)' }}>
                      <span style={{ color: 'rgba(201,168,76,0.5)', fontSize: 9 }}>59:24 — </span>
                      He is Allah, the Creator (Al-Khaliq), the Evolver (Al-Bari), the Fashioner of Forms (Al-Musawwir). To Him belong the best Names. Whatever is in the heavens and earth exalts Him. And He is the Exalted in Might, the Wise.
                    </p>
                  </div>
                  <p className="font-mono text-[10px] leading-relaxed" style={{ color: 'rgba(245,240,232,0.35)' }}>
                    Quran 59:23–24 is the most concentrated Names passage in the Quran — 14 Names in two verses. It is the origin verse of the Names Reference: the passage that establishes the Names as the compositional elements of divine reality. The Leaf Sequence (Al-Khaliq → Al-Bari → Al-Musawwir) appears directly in 59:24.
                  </p>
                </div>
                <div className="lg:w-64 flex-shrink-0">
                  <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(201,168,76,0.4)' }}>Names in 59:23–24</p>
                  <div className="flex flex-col gap-1">
                    {[
                      { id: 'malik', name: 'Al-Malik', ar: 'الْمَلِك', verse: '59:23' },
                      { id: 'quddus', name: 'Al-Quddus', ar: 'الْقُدُّوس', verse: '59:23' },
                      { id: 'salam', name: 'As-Salam', ar: 'السَّلَام', verse: '59:23' },
                      { id: 'mumin', name: 'Al-Mumin', ar: 'الْمُؤْمِن', verse: '59:23' },
                      { id: 'muhaymin', name: 'Al-Muhaymin', ar: 'الْمُهَيْمِن', verse: '59:23' },
                      { id: 'jabbar', name: 'Al-Jabbar', ar: 'الْجَبَّار', verse: '59:23' },
                      { id: 'mutakabbir', name: 'Al-Mutakabbir', ar: 'الْمُتَكَبِّر', verse: '59:23' },
                      { id: 'khaliq', name: 'Al-Khaliq', ar: 'الْخَالِق', verse: '59:24' },
                      { id: 'bari', name: 'Al-Bari', ar: 'الْبَارِئ', verse: '59:24' },
                      { id: 'musawwir', name: 'Al-Musawwir', ar: 'الْمُصَوِّر', verse: '59:24' },
                    ].map(item => {
                      const nameEntry = NAMES_DATA.find(n => n.id === item.id);
                      return (
                        <button
                          key={item.id}
                          onClick={() => nameEntry && scrollToName(nameEntry.id)}
                          className="flex items-center gap-2 p-1.5 text-left transition-all group"
                          style={{ border: '1px solid rgba(44,74,62,0.2)', borderRadius: 3 }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(201,168,76,0.3)'; (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.04)'; }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(44,74,62,0.2)'; (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                        >
                          <span className="font-mono text-[8px] px-1" style={{ background: item.verse === '59:24' ? 'rgba(45,122,71,0.15)' : 'rgba(201,168,76,0.08)', color: item.verse === '59:24' ? '#2d7a47' : 'rgba(201,168,76,0.6)', borderRadius: 2, whiteSpace: 'nowrap' }}>{item.verse}</span>
                          <span style={{ fontFamily: 'serif', fontSize: 13, color: 'rgba(201,168,76,0.5)' }}>{item.ar}</span>
                          <span className="font-mono text-[9px] uppercase tracking-wide" style={{ color: 'rgba(245,240,232,0.4)' }}>{item.name}</span>
                          {nameEntry && <span className="font-mono text-[8px] ml-auto" style={{ color: 'rgba(245,240,232,0.2)' }}>→</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── 99 NAMES HADITH — CLOSING ANCHOR ───────────────────────────── */}
        <section className="py-12" style={{ borderTop: '1px solid rgba(90,90,138,0.15)' }}>
          <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-[9px] tracking-widest uppercase px-2 py-0.5" style={{ background: 'rgba(90,90,138,0.12)', border: '1px solid rgba(90,90,138,0.35)', color: '#8a8ab8' }}>
                ✦ Closing Anchor
              </span>
              <span className="font-mono text-[8px] tracking-widest uppercase" style={{ color: 'rgba(90,90,138,0.5)' }}>The Foundation of the Entire Names Framework</span>
            </div>
            <div className="p-6 relative overflow-hidden" style={{ border: '1px solid rgba(90,90,138,0.25)', background: 'rgba(28,43,62,0.4)', borderRadius: 4 }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, rgba(90,90,138,0.5), rgba(201,168,76,0.3), rgba(90,90,138,0.5))' }} />
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="flex-1">
                  <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(90,90,138,0.6)' }}>The 99 Names Hadith — Sahih Bukhari & Muslim</p>
                  <div className="mb-4 p-4" style={{ background: 'rgba(6,13,18,0.4)', border: '1px solid rgba(90,90,138,0.15)', borderRadius: 4 }}>
                    <p className="font-mono text-xs leading-relaxed mb-3" style={{ color: 'rgba(245,240,232,0.6)', fontStyle: 'italic' }}>
                      &ldquo;Allah has ninety-nine Names — one hundred minus one. Whoever memorizes (احصاها — ahsaha) them will enter Paradise. He is Witr (Odd) and loves the odd number.&rdquo;
                    </p>
                    <p className="font-mono text-[9px]" style={{ color: 'rgba(245,240,232,0.3)' }}>
                      — Sahih Bukhari 2736, Sahih Muslim 2677
                    </p>
                  </div>
                  <div className="mb-4 p-4" style={{ background: 'rgba(90,90,138,0.04)', border: '1px solid rgba(90,90,138,0.12)', borderRadius: 4 }}>
                    <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: 'rgba(90,90,138,0.5)' }}>On the word احصاها (Ahsaha)</p>
                    <p className="font-mono text-[10px] leading-relaxed" style={{ color: 'rgba(245,240,232,0.4)' }}>
                      The word احصاها is often translated as &ldquo;memorizes&rdquo; but its root (ح-ص-ي) means to count, to enumerate, to comprehend fully — to hold something in its entirety. The IRL Lens is a methodology for ahsaha — for recognizing, enumerating, and comprehending the Names as they operate in creation. Every sequence in the IRL Lens is a form of ahsaha in practice.
                    </p>
                  </div>
                  <p className="font-mono text-[10px] leading-relaxed" style={{ color: 'rgba(245,240,232,0.3)' }}>
                    This hadith is the foundation of the entire Names framework. The Names Reference is a tool for ahsaha — for recognizing, enumerating, and comprehending the Names as they operate in creation. Every sequence in the IRL Lens is a form of ahsaha in practice.
                  </p>
                </div>
                <div className="lg:w-64 flex-shrink-0">
                  <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(90,90,138,0.4)' }}>Ahsaha in Practice</p>
                  <div className="flex flex-col gap-2">
                    {[
                      { label: 'Encounter', desc: 'First contact with creation — the beginning of ahsaha' },
                      { label: 'Examination', desc: 'Three Questions — the methodology of ahsaha' },
                      { label: 'Recognition', desc: 'Naming what you see — ahsaha made active' },
                      { label: 'Re-Assembly', desc: 'Seeing the blueprint — ahsaha deepened' },
                      { label: 'Uboor', desc: 'Crossing over — ahsaha completed in action' },
                    ].map((item, i) => (
                      <div key={i} className="p-2" style={{ border: '1px solid rgba(90,90,138,0.15)', borderRadius: 3, background: 'rgba(90,90,138,0.04)' }}>
                        <p className="font-mono text-[9px] uppercase tracking-widest mb-0.5" style={{ color: 'rgba(90,90,138,0.6)' }}>{item.label}</p>
                        <p className="font-mono text-[9px] leading-relaxed" style={{ color: 'rgba(245,240,232,0.3)' }}>{item.desc}</p>
                      </div>
                    ))}
                  </div>
                  {/* Alhamdulillah closing */}
                  <div className="mt-4 p-3 text-center" style={{ border: '1px solid rgba(201,168,76,0.3)', background: 'rgba(201,168,76,0.06)', borderRadius: 4, boxShadow: '0 0 16px rgba(201,168,76,0.08)' }}>
                    <div className="text-xl mb-1" style={{ fontFamily: 'serif', color: '#c9a84c', textShadow: '0 0 10px rgba(201,168,76,0.3)' }}>الْحَمْدُ لِلَّه</div>
                    <div className="font-mono text-[8px] uppercase tracking-widest" style={{ color: 'rgba(201,168,76,0.5)' }}>✦ Alhamdulillah</div>
                    <div className="font-mono text-[8px] mt-1" style={{ color: 'rgba(245,240,232,0.25)' }}>The closing state of every recognition arc</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

// ─── Main Page Export (Suspense wrapper for useSearchParams) ──────────────────

export default function NamesReferencePage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen deep-green-bg flex items-center justify-center">
        <p className="font-mono text-xs text-white/30">Loading Names Reference...</p>
      </div>
    }>
      <NamesReferenceInner />
    </Suspense>
  );
}
